<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useRouter, useRoute } from 'vue-router';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { emitter } from 'shared/helpers/mitt';
import { formatChatListDate } from 'shared/helpers/timeHelper';
import { generateURLParams, parseURLParams } from '../helpers/searchHelper';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Policy from 'dashboard/components/policy.vue';
import SzDatePicker from 'dashboard/components/widgets/conversation/solarz/SzDatePicker.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const PER_PAGE = 15;
const selectedTab = ref(route.params.tab || 'all');
const query = ref(route.query.q || '');
const searchInput = ref(null);
const pages = ref({
  contacts: 1,
  conversations: 1,
  messages: 1,
  articles: 1,
});

const contactRecords = useMapGetter('conversationSearch/getContactRecords');
const conversationRecords = useMapGetter('conversationSearch/getConversationRecords');
const messageRecords = useMapGetter('conversationSearch/getMessageRecords');
const articleRecords = useMapGetter('conversationSearch/getArticleRecords');
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('conversationSearch/getUIFlags');

const addTypeToRecords = (records, type) =>
  records.value.map(item => ({ ...useCamelCase(item, { deep: true }), type }));

const mappedContacts = computed(() => addTypeToRecords(contactRecords, 'contact'));
const mappedConversations = computed(() => addTypeToRecords(conversationRecords, 'conversation'));
const mappedMessages = computed(() => addTypeToRecords(messageRecords, 'message'));
const mappedArticles = computed(() => addTypeToRecords(articleRecords, 'article'));

// SZ derived filters — operate on results already returned by the search API.
const isGroupConv = c => {
  const id = c?.meta?.sender?.identifier || '';
  return id.endsWith('@g.us') || /^\d{15,22}$/.test(id);
};
const isMineConv = c => {
  const uid = currentUser.value?.id;
  if (!uid) return false;
  const assigneeId = c?.meta?.assignee?.id;
  const coIds = (c?.meta?.co_assignees || []).map(u => u.id);
  return assigneeId === uid || coIds.includes(uid);
};
const mappedConversationsMine = computed(() => mappedConversations.value.filter(isMineConv));
const mappedConversationsUnassigned = computed(() => mappedConversations.value.filter(c => !c?.meta?.assignee?.id));
const mappedConversationsGroups = computed(() => mappedConversations.value.filter(isGroupConv));

// SZ — helpers do redesign dos resultados (Figma 2026-05).

const channelSvg = (type) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('whatsapp')) {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.296-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/></svg>';
  }
  if (t.includes('email')) {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>';
  }
  if (t.includes('facebook')) {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 10-11.563 9.875V14.89H7.898V12h2.539V9.797c0-2.506 1.493-3.89 3.776-3.89 1.094 0 2.238.196 2.238.196v2.46h-1.26c-1.243 0-1.63.771-1.63 1.563V12h2.773l-.443 2.89h-2.33v6.985A10.002 10.002 0 0022 12z"/></svg>';
  }
  if (t.includes('instagram')) {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>';
  }
  if (t.includes('twilio') || t.includes('sms')) {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>';
  }
  // default — balão de chat genérico
  return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>';
};

const inboxById = (id) => {
  if (!id) return null;
  try { return store.getters['inboxes/getInbox'](Number(id)) || null; }
  catch (e) { return null; }
};
const inboxFor = (item) => {
  const id = item?.inboxId ?? item?.inbox_id
    ?? item?.conversation?.inboxId ?? item?.conversation?.inbox_id;
  return inboxById(id);
};
const inboxNameOf = (item) => inboxFor(item)?.name || '';
const channelTypeOf = (item) => String(
  inboxFor(item)?.channelType || inboxFor(item)?.channel_type || ''
).toLowerCase();

const escapeHtml = s => String(s ?? '').replace(/[&<>"']/g, m => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[m]));
const escapeRe = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const highlightHtml = (text) => {
  const q = (query.value || '').trim();
  const safe = escapeHtml(text);
  if (!q) return safe;
  return safe.replace(new RegExp(escapeRe(q), 'gi'), m => `<span class=\"sz-hl\">${m}</span>`);
};

const secondaryFromContact = (contact) =>
  contact?.phoneNumber || contact?.phone_number || contact?.email || '';

const messageContactName = (msg) => {
  const sender = msg?.conversation?.meta?.sender || msg?.sender;
  return sender?.name || '';
};
const messageDisplayId = (msg) =>
  msg?.conversation?.displayId || msg?.conversation?.display_id
  || msg?.conversationId || msg?.conversation_id || '';

const truncate = (s, n = 140) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? t.slice(0, n) + '…' : t;
};

// SZ — sidebar filter state (rascunho + snapshot aplicado).
// Usa a mesma fonte do filtro da home (agents/getAgents) e dispara fetch se vazio.
const agentsList = useMapGetter('agents/getAgents');
// Oculta agentes sem nome válido (placeholders / "?") e bots de sistema (ex: "iris rede neural").
const EXCLUDED_AGENT_PATTERNS = [/iris\s+rede\s+neural/i];
const agentOptions = computed(() =>
  (agentsList.value || []).filter(a => {
    if (!a || !a.id) return false;
    const name = String(a.name || '').trim();
    if (!name || name === '?') return false;
    if (EXCLUDED_AGENT_PATTERNS.some(re => re.test(name))) return false;
    return true;
  })
);
const isAgentsFetching = ref(false);
onMounted(async () => {
  if (!agentsList.value?.length) {
    isAgentsFetching.value = true;
    try { await store.dispatch('agents/get'); }
    catch { /* silencioso — dropdown mostra "Nenhum responsável disponível" */ }
    finally { isAgentsFetching.value = false; }
  }
});

// SZ — custom dropdown do filtro Responsável (replica visual da home).
const assigneeDropdownOpen = ref(false);
const selectedAgent = computed(() =>
  agentOptions.value.find(a => a.id === draft.value.assigneeId) || null
);
const toggleAssignee = (a) => {
  draft.value.assigneeId = (draft.value.assigneeId === a.id) ? null : a.id;
  assigneeDropdownOpen.value = false;
};
const clearAssignee = () => { draft.value.assigneeId = null; };
const onDocClickAssignee = (ev) => {
  if (!assigneeDropdownOpen.value) return;
  const wrap = document.querySelector('.sz-sb-multi-wrap');
  if (wrap && !wrap.contains(ev.target)) assigneeDropdownOpen.value = false;
};
onMounted(() => { document.addEventListener('click', onDocClickAssignee); });
onUnmounted(() => { document.removeEventListener('click', onDocClickAssignee); });

const draft = ref({
  assigneeId: null,
  preset: null,           // 'today' | 'thisMonth' | 'lastMonth' | 'lastWeek' | null
  fromDate: '',           // 'YYYY-MM-DD'
  toDate: '',
});
const applied = ref({
  assigneeId: null,
  fromDate: '',
  toDate: '',
});

const periodPresets = [
  { key: 'today',     label: 'Hoje' },
  { key: 'thisMonth', label: 'Este mês' },
  { key: 'lastMonth', label: 'Mês passado' },
  { key: 'lastWeek',  label: 'Última semana' },
];

const pad2 = n => String(n).padStart(2, '0');
const fmtDate = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const startOfDay = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = d => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

const setPeriodPreset = (key) => {
  draft.value.preset = key;
  const now = new Date();
  let from, to;
  if (key === 'today') {
    from = startOfDay(now); to = endOfDay(now);
  } else if (key === 'thisMonth') {
    from = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    to = endOfDay(now);
  } else if (key === 'lastMonth') {
    from = startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    to = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
  } else if (key === 'lastWeek') {
    from = startOfDay(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
    to = endOfDay(now);
  } else {
    return;
  }
  draft.value.fromDate = fmtDate(from);
  draft.value.toDate = fmtDate(to);
};

const onDraftDateInput = () => { draft.value.preset = null; };

const applyFilters = () => {
  applied.value = {
    assigneeId: draft.value.assigneeId || null,
    fromDate: draft.value.fromDate || '',
    toDate: draft.value.toDate || '',
  };
};
const clearFilters = () => {
  draft.value = { assigneeId: null, preset: null, fromDate: '', toDate: '' };
  applied.value = { assigneeId: null, fromDate: '', toDate: '' };
};

// Predicados — usam applied (snapshot), não draft.
const isWithinPeriod = (tsLike) => {
  const { fromDate, toDate } = applied.value;
  if (!fromDate && !toDate) return true;
  if (!tsLike) return false;
  const ts = typeof tsLike === 'number' ? (tsLike < 1e12 ? tsLike * 1000 : tsLike) : Date.parse(tsLike);
  if (!Number.isFinite(ts)) return false;
  if (fromDate) {
    const from = startOfDay(new Date(fromDate + 'T00:00:00')).getTime();
    if (ts < from) return false;
  }
  if (toDate) {
    const to = endOfDay(new Date(toDate + 'T00:00:00')).getTime();
    if (ts > to) return false;
  }
  return true;
};

const convMatchesAssignee = (c) => {
  const uid = applied.value.assigneeId;
  if (!uid) return true;
  const aid = c?.meta?.assignee?.id;
  if (aid === uid) return true;
  const coIds = (c?.meta?.co_assignees || []).map(u => u.id);
  return coIds.includes(uid);
};
const convMatchesPeriod = (c) => isWithinPeriod(c?.lastActivityAt || c?.last_activity_at || c?.createdAt || c?.created_at);
const msgMatchesAssignee = (m) => {
  const uid = applied.value.assigneeId;
  if (!uid) return true;
  // mensagem pertence à conversa — herda assignee dela
  return convMatchesAssignee(m?.conversation || {});
};
const msgMatchesPeriod = (m) => isWithinPeriod(m?.createdAt || m?.created_at);
const contactMatchesAssignee = (ct) => {
  const uid = applied.value.assigneeId;
  if (!uid) return true;
  // Contato match: qualquer das conversas dos resultados pertence ao agente e referencia esse contato.
  const cid = ct?.id;
  return mappedConversations.value.some(c => (c?.meta?.sender?.id === cid || c?.contact?.id === cid) && convMatchesAssignee(c));
};

// Listas filtradas — substituem mappedX nas listas exibidas.
const filteredConversations = computed(() => mappedConversations.value.filter(c => convMatchesAssignee(c) && convMatchesPeriod(c)));
const filteredMessages = computed(() => mappedMessages.value.filter(m => msgMatchesAssignee(m) && msgMatchesPeriod(m)));
const filteredContacts = computed(() => mappedContacts.value.filter(contactMatchesAssignee));
const filteredMine = computed(() => mappedConversationsMine.value.filter(c => convMatchesAssignee(c) && convMatchesPeriod(c)));
const filteredUnassigned = computed(() => mappedConversationsUnassigned.value.filter(c => convMatchesAssignee(c) && convMatchesPeriod(c)));
const filteredGroups = computed(() => mappedConversationsGroups.value.filter(c => convMatchesAssignee(c) && convMatchesPeriod(c)));


const isSelectedTabAll = computed(() => selectedTab.value === 'all');

const sliceRecordsIfAllTab = items =>
  isSelectedTabAll.value ? items.value.slice(0, 8) : items.value;

const contacts = computed(() => sliceRecordsIfAllTab(mappedContacts));
const conversations = computed(() => sliceRecordsIfAllTab(mappedConversations));
const messages = computed(() => sliceRecordsIfAllTab(mappedMessages));
const articles = computed(() => sliceRecordsIfAllTab(mappedArticles));

const { shouldShow, isFeatureFlagEnabled } = usePolicy();

const isFetchingAny = computed(() => {
  const { contact, message, conversation, article, isFetching } = uiFlags.value;
  return isFetching || contact.isFetching || message.isFetching || conversation.isFetching || article.isFetching;
});

const totalSearchResultsCount = computed(() => {
  return filteredContacts.value.length + filteredConversations.value.length +
    filteredMessages.value.length + mappedArticles.value.length;
});

const showEmptySearchResults = computed(() =>
  totalSearchResultsCount.value === 0 && uiFlags.value.isSearchCompleted &&
  !isFetchingAny.value && query.value
);

const showLoadMore = computed(() => {
  if (!query.value || isFetchingAny.value || selectedTab.value === 'all') return false;
  // SZ: mine/unassigned/groups são derivados client-side; não paginam.
  const records = {
    contacts: mappedContacts.value,
    conversations: mappedConversations.value,
    messages: mappedMessages.value,
    articles: mappedArticles.value,
  }[selectedTab.value];
  if (!records) return false;
  return records.length > 0 && records.length === pages.value[selectedTab.value] * PER_PAGE;
});

const showViewMore = computed(() => ({
  contacts: mappedContacts.value?.length > 8 && isSelectedTabAll.value,
  conversations: mappedConversations.value?.length > 8 && isSelectedTabAll.value,
  messages: mappedMessages.value?.length > 8 && isSelectedTabAll.value,
  articles: mappedArticles.value?.length > 8 && isSelectedTabAll.value,
}));

const filters = ref({
  from: null,
  in: null,
  dateRange: { type: null, from: null, to: null },
});

// SZ tabs — order matches the Figma design.
const tabs = [
  { key: 'all', label: 'Tudo' },
  { key: 'mine', label: 'Minhas' },
  { key: 'unassigned', label: 'Não atribuídas' },
  { key: 'conversations', label: 'Conversas' },
  { key: 'groups', label: 'Grupos' },
  { key: 'messages', label: 'Mensagens' },
  { key: 'contacts', label: 'Contatos' },
];

const tabCounts = computed(() => ({
  all: filteredContacts.value.length + filteredConversations.value.length + filteredMessages.value.length + mappedArticles.value.length,
  mine: filteredMine.value.length,
  unassigned: filteredUnassigned.value.length,
  conversations: filteredConversations.value.length,
  groups: filteredGroups.value.length,
  messages: filteredMessages.value.length,
  contacts: filteredContacts.value.length,
}));

const clearSearchResult = () => {
  pages.value = { contacts: 1, conversations: 1, messages: 1, articles: 1 };
  store.dispatch('conversationSearch/clearSearchResults');
};

const buildSearchPayload = (basePayload = {}) => {
  const payload = { ...basePayload };
  if (filters.value.dateRange?.from) payload.since = filters.value.dateRange.from;
  if (filters.value.dateRange?.to) payload.until = filters.value.dateRange.to;
  return payload;
};

const updateURL = () => {
  const params = {
    accountId: route.params.accountId,
    ...(selectedTab.value !== 'all' && { tab: selectedTab.value }),
  };
  const queryParams = {
    ...(query.value?.trim() && { q: query.value.trim() }),
  };
  router.replace({ name: 'search', params, query: queryParams });
};

let searchTimeout = null;
const onSearchInput = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    clearSearchResult();
    updateURL();
    if (!query.value || query.value.length < 2) return;
    useTrack(CONVERSATION_EVENTS.SEARCH_CONVERSATION);
    const searchPayload = buildSearchPayload({ q: query.value, page: 1 });
    store.dispatch('conversationSearch/fullSearch', searchPayload);
  }, 350);
};

const onBack = () => {
  if (window.history.length > 2) router.go(-1);
  else router.push({ name: 'home' });
  clearSearchResult();
};

const loadMore = () => {
  const SEARCH_ACTIONS = {
    contacts: 'conversationSearch/contactSearch',
    conversations: 'conversationSearch/conversationSearch',
    messages: 'conversationSearch/messageSearch',
    articles: 'conversationSearch/articleSearch',
  };
  if (uiFlags.value.isFetching || selectedTab.value === 'all') return;
  const tab = selectedTab.value;
  pages.value[tab] += 1;
  const payload = buildSearchPayload({ q: query.value, page: pages.value[tab] });
  store.dispatch(SEARCH_ACTIONS[tab], payload);
};

function goToContact(id) {
  router.push(frontendURL(`accounts/${route.params.accountId}/contacts/${id}`));
}
function goToConversation(id) {
  router.push(frontendURL(`accounts/${route.params.accountId}/conversations/${id}`));
}
function getInitial(name) {
  return (name || '?')[0].toUpperCase();
}

// SZ unify: reage à query do SzTopBar via emitter (instantâneo) e watch da URL (compartilhamento de link).
const onSzExternalQuery = q => {
  const next = (q || '').toString();
  if (next === query.value) return;
  query.value = next;
  onSearchInput();
};
let stopQueryWatch = null;

onMounted(() => {
  store.dispatch('conversationSearch/clearSearchResults');
  const parsedFilters = parseURLParams(
    route.query,
    isFeatureFlagEnabled(FEATURE_FLAGS.ADVANCED_SEARCH)
  );
  filters.value = parsedFilters;
  if (route.query.q) {
    query.value = route.query.q;
    onSearchInput();
  }
  emitter.on('sz:search-query', onSzExternalQuery);
  // Notifica TopBar para pré-popular seu próprio input com a query atual da URL
  emitter.emit('sz:search-view-mounted', query.value);
  stopQueryWatch = watch(
    () => route.query.q,
    nv => onSzExternalQuery(nv || '')
  );
});

onUnmounted(() => {
  query.value = '';
  store.dispatch('conversationSearch/clearSearchResults');
  emitter.off('sz:search-query', onSzExternalQuery);
  emitter.emit('sz:search-view-unmounted');
  if (stopQueryWatch) stopQueryWatch();
});
</script>

<template>
  <div class="sz-fullsearch">
    <!-- Toolbar: back + tabs + compact search -->
    <div class="sz-fullsearch-toolbar">
      <button class="sz-fullsearch-back" @click="onBack">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
      </button>
      <div class="sz-fullsearch-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="sz-fullsearch-tab"
          :class="{ active: selectedTab === tab.key }"
          @click="selectedTab = tab.key; updateURL()"
        >
          <span class="sz-fullsearch-tab-label">{{ tab.label }}</span>
          <span class="sz-fullsearch-tab-count">{{ tabCounts[tab.key] || 0 }}</span>
        </button>
      </div>
      <!-- SZ unify: input local removido; busca é guiada pelo SzTopBar (header) via emitter/URL -->
    </div>

    <!-- Body (results + sidebar) -->
    <div class="sz-fullsearch-body">
    <div class="sz-fullsearch-results">
      <!-- Loading -->
      <div v-if="isFetchingAny && totalSearchResultsCount === 0" class="sz-fullsearch-loading">
        <div class="sz-fullsearch-spinner" />
        <span>Buscando...</span>
      </div>

      <!-- Empty -->
      <div v-else-if="showEmptySearchResults" class="sz-fullsearch-empty">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        <span>Nenhum resultado para "{{ query }}"</span>
      </div>

      <!-- No query -->
      <div v-else-if="!query" class="sz-fullsearch-empty">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        <span>Digite para buscar contatos, conversas e mensagens</span>
      </div>

      <!-- Results content -->
      <template v-else>
        <!-- SZ Minhas -->
        <div v-if="selectedTab === 'mine' && filteredMine.length" class="sz-fullsearch-section">
          <div class="sz-fullsearch-section-header">
            <span class="sz-fullsearch-section-title">Minhas</span>
            <span class="sz-fullsearch-section-count">{{ filteredMine.length }}</span>
          </div>
          <div
            v-for="conv in filteredMine"
            :key="'mine'+conv.id"
            class="sz-fullsearch-item"
            @click="goToConversation(conv.id)"
          >
            <div class="sz-fullsearch-avatar">
              <img v-if="conv.meta?.sender?.thumbnail" :src="conv.meta.sender.thumbnail" />
              <span v-else>{{ getInitial(conv.meta?.sender?.name || conv.contact?.name) }}</span>
            </div>
            <div class="sz-fullsearch-item-body">
              <div class="sz-fullsearch-item-primary">
                <span class="sz-fullsearch-id">#{{ conv.displayId || conv.id }}</span>
                <span class="sz-fullsearch-name" v-html="highlightHtml(conv.meta?.sender?.name || conv.contact?.name || 'Conversa')"></span>
                <!-- SZ: data da última atividade pra distinguir conversas do mesmo contato -->
                <span
                  v-if="conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt"
                  class="sz-fullsearch-date"
                >{{ formatChatListDate(conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt) }}</span>
              </div>
              <div class="sz-fullsearch-item-meta">
                <span class="sz-channel-icon" :data-type="channelTypeOf(conv)" v-html="channelSvg(channelTypeOf(conv))"></span>
                <span class="sz-meta-text">{{ inboxNameOf(conv) }}</span>
                <span v-if="secondaryFromContact(conv.meta?.sender)" class="sz-meta-sep">•</span>
                <span class="sz-meta-text">{{ secondaryFromContact(conv.meta?.sender) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- SZ Não atribuídas -->
        <div v-if="selectedTab === 'unassigned' && filteredUnassigned.length" class="sz-fullsearch-section">
          <div class="sz-fullsearch-section-header">
            <span class="sz-fullsearch-section-title">Não atribuídas</span>
            <span class="sz-fullsearch-section-count">{{ filteredUnassigned.length }}</span>
          </div>
          <div
            v-for="conv in filteredUnassigned"
            :key="'unas'+conv.id"
            class="sz-fullsearch-item"
            @click="goToConversation(conv.id)"
          >
            <div class="sz-fullsearch-avatar">
              <img v-if="conv.meta?.sender?.thumbnail" :src="conv.meta.sender.thumbnail" />
              <span v-else>{{ getInitial(conv.meta?.sender?.name || conv.contact?.name) }}</span>
            </div>
            <div class="sz-fullsearch-item-body">
              <div class="sz-fullsearch-item-primary">
                <span class="sz-fullsearch-id">#{{ conv.displayId || conv.id }}</span>
                <span class="sz-fullsearch-name" v-html="highlightHtml(conv.meta?.sender?.name || conv.contact?.name || 'Conversa')"></span>
                <!-- SZ: data da última atividade pra distinguir conversas do mesmo contato -->
                <span
                  v-if="conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt"
                  class="sz-fullsearch-date"
                >{{ formatChatListDate(conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt) }}</span>
              </div>
              <div class="sz-fullsearch-item-meta">
                <span class="sz-channel-icon" :data-type="channelTypeOf(conv)" v-html="channelSvg(channelTypeOf(conv))"></span>
                <span class="sz-meta-text">{{ inboxNameOf(conv) }}</span>
                <span v-if="secondaryFromContact(conv.meta?.sender)" class="sz-meta-sep">•</span>
                <span class="sz-meta-text">{{ secondaryFromContact(conv.meta?.sender) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- SZ Grupos -->
        <div v-if="selectedTab === 'groups' && filteredGroups.length" class="sz-fullsearch-section">
          <div class="sz-fullsearch-section-header">
            <span class="sz-fullsearch-section-title">Grupos</span>
            <span class="sz-fullsearch-section-count">{{ filteredGroups.length }}</span>
          </div>
          <div
            v-for="conv in filteredGroups"
            :key="'grou'+conv.id"
            class="sz-fullsearch-item"
            @click="goToConversation(conv.id)"
          >
            <div class="sz-fullsearch-avatar">
              <img v-if="conv.meta?.sender?.thumbnail" :src="conv.meta.sender.thumbnail" />
              <span v-else>{{ getInitial(conv.meta?.sender?.name || conv.contact?.name) }}</span>
            </div>
            <div class="sz-fullsearch-item-body">
              <div class="sz-fullsearch-item-primary">
                <span class="sz-fullsearch-id">#{{ conv.displayId || conv.id }}</span>
                <span class="sz-fullsearch-name" v-html="highlightHtml(conv.meta?.sender?.name || conv.contact?.name || 'Conversa')"></span>
                <!-- SZ: data da última atividade pra distinguir conversas do mesmo contato -->
                <span
                  v-if="conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt"
                  class="sz-fullsearch-date"
                >{{ formatChatListDate(conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt) }}</span>
              </div>
              <div class="sz-fullsearch-item-meta">
                <span class="sz-channel-icon" :data-type="channelTypeOf(conv)" v-html="channelSvg(channelTypeOf(conv))"></span>
                <span class="sz-meta-text">{{ inboxNameOf(conv) }}</span>
                <span v-if="secondaryFromContact(conv.meta?.sender)" class="sz-meta-sep">•</span>
                <span class="sz-meta-text">{{ secondaryFromContact(conv.meta?.sender) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Contacts -->
        <div v-if="(selectedTab === 'all' || selectedTab === 'contacts') && filteredContacts.length" class="sz-fullsearch-section">
          <div class="sz-fullsearch-section-header">
            <span class="sz-fullsearch-section-title">Contatos</span>
            <span class="sz-fullsearch-section-count">{{ filteredContacts.length }}</span>
          </div>
          <div
            v-for="contact in filteredContacts"
            :key="'c'+contact.id"
            class="sz-fullsearch-item"
            @click="goToContact(contact.id)"
          >
            <div class="sz-fullsearch-avatar">
              <img v-if="contact.thumbnail" :src="contact.thumbnail" />
              <span v-else>{{ getInitial(contact.name) }}</span>
            </div>
            <div class="sz-fullsearch-item-body">
              <div class="sz-fullsearch-item-primary">
                <span class="sz-fullsearch-name" v-html="highlightHtml(contact.name || 'Sem nome')"></span>
              </div>
              <div class="sz-fullsearch-item-meta">
                <span class="sz-meta-text">{{ contact.phoneNumber || contact.email || '' }}</span>
              </div>
            </div>
          </div>
          <button v-if="showViewMore.contacts" class="sz-fullsearch-viewmore" @click="selectedTab = 'contacts'">
            Ver mais contatos
          </button>
        </div>

        <!-- Conversations -->
        <div v-if="(selectedTab === 'all' || selectedTab === 'conversations') && filteredConversations.length" class="sz-fullsearch-section">
          <div class="sz-fullsearch-section-header">
            <span class="sz-fullsearch-section-title">Conversas</span>
            <span class="sz-fullsearch-section-count">{{ filteredConversations.length }}</span>
          </div>
          <div
            v-for="conv in filteredConversations"
            :key="'v'+conv.id"
            class="sz-fullsearch-item"
            @click="goToConversation(conv.id)"
          >
            <div class="sz-fullsearch-avatar">
              <img v-if="conv.meta?.sender?.thumbnail" :src="conv.meta.sender.thumbnail" />
              <span v-else>{{ getInitial(conv.meta?.sender?.name || conv.contact?.name) }}</span>
            </div>
            <div class="sz-fullsearch-item-body">
              <div class="sz-fullsearch-item-primary">
                <span class="sz-fullsearch-id">#{{ conv.displayId || conv.id }}</span>
                <span class="sz-fullsearch-name" v-html="highlightHtml(conv.meta?.sender?.name || conv.contact?.name || 'Conversa')"></span>
                <!-- SZ: data da última atividade pra distinguir conversas do mesmo contato -->
                <span
                  v-if="conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt"
                  class="sz-fullsearch-date"
                >{{ formatChatListDate(conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt) }}</span>
              </div>
              <div class="sz-fullsearch-item-meta">
                <span class="sz-channel-icon" :data-type="channelTypeOf(conv)" v-html="channelSvg(channelTypeOf(conv))"></span>
                <span class="sz-meta-text">{{ inboxNameOf(conv) }}</span>
                <span v-if="secondaryFromContact(conv.meta?.sender)" class="sz-meta-sep">•</span>
                <span class="sz-meta-text">{{ secondaryFromContact(conv.meta?.sender) }}</span>
              </div>
            </div>
          </div>
          <button v-if="showViewMore.conversations" class="sz-fullsearch-viewmore" @click="selectedTab = 'conversations'">
            Ver mais conversas
          </button>
        </div>

        <!-- Messages -->
        <div v-if="(selectedTab === 'all' || selectedTab === 'messages') && filteredMessages.length" class="sz-fullsearch-section">
          <div class="sz-fullsearch-section-header">
            <span class="sz-fullsearch-section-title">Mensagens</span>
            <span class="sz-fullsearch-section-count">{{ filteredMessages.length }}</span>
          </div>
          <div
            v-for="msg in filteredMessages"
            :key="'m'+msg.id"
            class="sz-fullsearch-item"
            @click="goToConversation(msg.conversationId)"
          >
            <div class="sz-fullsearch-avatar sz-fullsearch-avatar-msg">
              <svg v-if="msg.private" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4"/><path d="M16 8a4 4 0 11-8 0"/></svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </div>
            <div class="sz-fullsearch-item-body">
              <div class="sz-fullsearch-item-primary sz-fullsearch-item-primary--msg" v-html="highlightHtml(truncate(msg.content, 140))"></div>
              <div class="sz-fullsearch-item-meta">
                <span class="sz-channel-icon" :data-type="channelTypeOf(msg)" v-html="channelSvg(channelTypeOf(msg))"></span>
                <span class="sz-meta-text">{{ inboxNameOf(msg) }}</span>
                <span class="sz-meta-sep">•</span>
                <span class="sz-meta-text">#{{ messageDisplayId(msg) }} {{ messageContactName(msg) }}</span>
              </div>
            </div>
          </div>
          <button v-if="showViewMore.messages" class="sz-fullsearch-viewmore" @click="selectedTab = 'messages'">
            Ver mais mensagens
          </button>
        </div>

        <!-- Load more (single tab) -->
        <div v-if="showLoadMore" class="sz-fullsearch-loadmore">
          <button @click="loadMore">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
            Carregar mais
          </button>
        </div>
      </template>
    </div>
    <!-- SZ Filters Sidebar -->
    <aside class="sz-search-sidebar">
      <h3 class="sz-sb-title">Filtros</h3>
      <div class="sz-sb-section">
        <label class="sz-sb-label">Responsável</label>
        <div class="sz-sb-multi-wrap">
          <div
            class="sz-sb-multi-field"
            @click="assigneeDropdownOpen = !assigneeDropdownOpen"
          >
            <div class="sz-sb-multi-tags">
              <template v-if="!selectedAgent">
                <span class="sz-sb-placeholder">Todos</span>
              </template>
              <template v-else>
                <span class="sz-sb-tag-agent" @click.stop>
                  <img v-if="selectedAgent.thumbnail" :src="selectedAgent.thumbnail" :alt="selectedAgent.name" class="sz-sb-tag-avatar" />
                  <span v-else class="sz-sb-tag-avatar sz-sb-tag-avatar--initial">{{ (selectedAgent.name || '?')[0] }}</span>
                  <span class="sz-sb-tag-text">{{ selectedAgent.name }}</span>
                  <span class="sz-sb-tag-x" @click.stop="clearAssignee">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </span>
                </span>
              </template>
            </div>
            <svg class="sz-sb-multi-chevron" :class="{ open: assigneeDropdownOpen }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div v-if="assigneeDropdownOpen" class="sz-sb-multi-dropdown">
            <template v-if="isAgentsFetching && !agentOptions.length">
              <div
                v-for="i in 4"
                :key="'sk-' + i"
                class="sz-sb-multi-option sz-sb-multi-option--skeleton"
              >
                <span class="sz-sb-skel sz-sb-skel--avatar"></span>
                <span class="sz-sb-skel sz-sb-skel--text"></span>
              </div>
            </template>
            <template v-else>
              <div
                v-for="a in agentOptions"
                :key="a.id"
                class="sz-sb-multi-option"
                :class="{ 'sz-sb-multi-option--on': draft.assigneeId === a.id }"
                @click="toggleAssignee(a)"
              >
                <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" class="sz-sb-option-avatar" />
                <span v-else class="sz-sb-option-avatar sz-sb-option-avatar--initial">{{ (a.name || '?')[0] }}</span>
                <span class="sz-sb-option-text">{{ a.name }}</span>
                <svg v-if="draft.assigneeId === a.id" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#388E3C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div v-if="!agentOptions.length" class="sz-sb-multi-empty">Nenhum responsável disponível</div>
            </template>
          </div>
        </div>
      </div>

      <div class="sz-sb-section">
        <label class="sz-sb-label">Período</label>
        <div class="sz-sb-presets">
          <button
            v-for="p in periodPresets"
            :key="p.key"
            type="button"
            class="sz-sb-pill"
            :class="{ active: draft.preset === p.key }"
            @click="setPeriodPreset(p.key)"
          >{{ p.label }}</button>
        </div>
        <div class="sz-sb-date-row">
          <div class="sz-sb-date-field">
            <span class="sz-sb-date-prefix">Início:</span>
            <SzDatePicker
              v-model="draft.fromDate"
              :max="draft.toDate || ''"
              placeholder="dd/mm/aaaa"
              @update:modelValue="onDraftDateInput"
            />
          </div>
          <div class="sz-sb-date-field">
            <span class="sz-sb-date-prefix">Fim:</span>
            <SzDatePicker
              v-model="draft.toDate"
              :min="draft.fromDate || ''"
              placeholder="dd/mm/aaaa"
              @update:modelValue="onDraftDateInput"
            />
          </div>
        </div>
      </div>

      <div class="sz-sb-actions">
        <button type="button" class="sz-sb-btn sz-sb-btn--ghost" @click="clearFilters">Limpar filtros</button>
        <button type="button" class="sz-sb-btn sz-sb-btn--primary" @click="applyFilters">Filtrar</button>
      </div>
    </aside>
    </div>
  </div>
</template>

<style scoped>
.sz-fullsearch {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--color-background, #FFFFFF);
}

/* Toolbar: back + tabs + compact search */
.sz-fullsearch-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--color-border-light, #E5E7EB);
  flex-shrink: 0;
}
.sz-fullsearch-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6B7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.sz-fullsearch-back:hover { background: #F3F4F6; }

/* Tabs */
.sz-fullsearch-tabs {
  display: flex;
  gap: 4px;
  flex: 1;
  min-width: 0;
}
/* SZ pills — Figma: light + badge preto sempre visível. */
.sz-fullsearch-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 24px;
  padding: 0 4px 0 8px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #000000;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.sz-fullsearch-tab:hover { background: rgba(56,142,60,0.08); }
.sz-fullsearch-tab.active {
  background: #DFEFE3;
  border-color: transparent;
}
.sz-fullsearch-tab-label {
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: inherit;
}
.sz-fullsearch-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-width: 16px;
  padding: 0 5px;
  background: #000000;
  color: #FFFFFF;
  border-radius: 24px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
}

/* Compact search input */
.sz-fullsearch-input-compact {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  width: 200px;
  padding: 0 10px;
  border: 1px solid var(--color-border-light, #D4D4D4);
  border-radius: 8px;
  background: var(--color-background, #FFFFFF);
  flex-shrink: 0;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.sz-fullsearch-input-compact:focus-within {
  border-color: #59AA59;
  box-shadow: 0 0 0 2px rgba(56, 142, 60, 0.10);
}
.sz-fullsearch-input-compact svg { color: #9CA3AF; flex-shrink: 0; }
.sz-fullsearch-input,
.sz-fullsearch-input:focus,
.sz-fullsearch-input:active,
.sz-fullsearch-input:hover {
  flex: 1;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
  padding: 0 !important;
  margin: 0 !important;
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  font-size: 13px;
  color: #1E293B;
  line-height: 1.5;
  border-radius: 0 !important;
  width: 100%;
}
.sz-fullsearch-input::placeholder { color: #9CA3AF; font-size: 12px; }
.sz-fullsearch-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: #F3F4F6;
  border-radius: 50%;
  color: #6B7280;
  cursor: pointer;
  flex-shrink: 0;
}
.sz-fullsearch-clear:hover { background: #E5E7EB; }

/* Results area */
.sz-fullsearch-results {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
}

/* Loading */
.sz-fullsearch-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 20px;
  color: #9CA3AF;
  font-size: 14px;
}
.sz-fullsearch-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #E2E8F0;
  border-top-color: #388E3C;
  border-radius: 50%;
  animation: sz-spin 0.6s linear infinite;
}
@keyframes sz-spin { to { transform: rotate(360deg); } }

/* Empty */
.sz-fullsearch-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 20px;
  color: #9CA3AF;
  font-size: 14px;
}

/* Section */
.sz-fullsearch-section {
  padding: 8px 0;
}
.sz-fullsearch-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 4px 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  letter-spacing: 0;
  text-transform: none;
}
.sz-fullsearch-section-title { color: inherit; }
.sz-fullsearch-section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-width: 16px;
  padding: 0 5px;
  background: #000000;
  color: #FFFFFF;
  border-radius: 24px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
}

/* SZ item row — Figma 2026-05 redesign */
.sz-fullsearch-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.1s;
}
.sz-fullsearch-item:hover { background: #F5F5F5; }

.sz-fullsearch-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #6B7280;
}
.sz-fullsearch-avatar img { width: 100%; height: 100%; object-fit: cover; }
.sz-fullsearch-avatar-msg { background: #DFEFE3; color: #388E3C; }

.sz-fullsearch-item-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  gap: 4px;
}
.sz-fullsearch-item-primary {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sz-fullsearch-item-primary--msg {
  display: block;
  font-weight: 400;
}
.sz-fullsearch-id {
  color: #6B7280;
  font-weight: 500;
  flex-shrink: 0;
}
.sz-fullsearch-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
/* SZ: badge cinza da última atividade — alinhado à direita do nome */
.sz-fullsearch-date {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #E5E7EB;
  color: #4B5563;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}
.dark .sz-fullsearch-date {
  background: #3D3F43;
  color: #BFBFC5;
}
.sz-fullsearch-item-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6B7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sz-meta-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.sz-meta-sep {
  color: #9CA3AF;
  flex-shrink: 0;
}
.sz-channel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #6B7280;
}
.sz-channel-icon[data-type*="whatsapp"] { color: #25D366; }

/* Highlight do termo buscado — Figma: bg verde claro + texto verde forte */
.sz-hl {
  background: #DFEFE3;
  color: #388E3C;
  padding: 0 2px;
  border-radius: 2px;
  font-weight: inherit;
}

/* View more / Load more */
.sz-fullsearch-viewmore {
  display: block;
  width: 100%;
  padding: 8px;
  margin-top: 4px;
  border: none;
  background: transparent;
  color: #388E3C;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  text-align: center;
  transition: background 0.1s;
}
.sz-fullsearch-viewmore:hover { background: #F0FDF4; }
.sz-fullsearch-loadmore {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}
.sz-fullsearch-loadmore button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: 1px solid #E5E7EB;
  background: #FFFFFF;
  border-radius: 8px;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.1s;
}
.sz-fullsearch-loadmore button:hover { background: #F9FAFB; }
</style>

<style>
/* Dark mode */
.dark .sz-fullsearch { background: #17181B; }
.dark .sz-fullsearch-toolbar { background: #191B1E; }
/* SZ Filters Sidebar — Figma 2026-05 */
.sz-fullsearch-body {
  display: flex;
  flex: 1;
  min-height: 0;
}
.sz-fullsearch-body > .sz-fullsearch-results {
  flex: 1;
  min-width: 0;
}
.sz-search-sidebar {
  width: 425px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 12px;
  background: #FFFFFF;
  border-left: 1px solid #E5E5E5;
  overflow-y: auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}
.sz-sb-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #000000;
  margin: 0;
}
.sz-sb-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sz-sb-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #000000;
}
/* Multi-select wrapper (Responsável) — replica visual do SzFilterSheet */
.sz-sb-multi-wrap { position: relative; }
.sz-sb-multi-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 4px 8px;
  background: #FFFFFF;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  cursor: pointer;
  box-sizing: border-box;
}
.sz-sb-multi-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}
.sz-sb-placeholder { font-size: 14px; color: #6B7280; }
.sz-sb-multi-chevron { color: #6B7280; flex-shrink: 0; transition: transform 0.15s; }
.sz-sb-multi-chevron.open { transform: rotate(180deg); }

/* Tag de agente */
.sz-sb-tag-agent {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 4px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  font-size: 12px;
}
.sz-sb-tag-avatar {
  width: 16px; height: 16px; border-radius: 999px; object-fit: cover;
}
.sz-sb-tag-avatar--initial {
  display: inline-flex; align-items: center; justify-content: center;
  background: #FF5500; color: #FFFFFF; font-size: 9px; font-weight: 600;
}
.sz-sb-tag-text {
  font-family: inherit;
  font-size: 12px; font-weight: 400; color: #000000;
  max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sz-sb-tag-x {
  display: inline-flex; align-items: center; justify-content: center;
  width: 12px; height: 12px;
  cursor: pointer; color: #4338CA;
}
.sz-sb-tag-x:hover { opacity: 0.7; }

/* Dropdown */
.sz-sb-multi-dropdown {
  position: absolute;
  left: 0; right: 0; top: calc(100% + 4px);
  z-index: 60;
  max-height: 220px;
  overflow-y: auto;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  padding: 4px;
}
.sz-sb-multi-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.sz-sb-multi-option:hover { background: rgba(56,142,60,0.06); }
.sz-sb-multi-option--on { background: rgba(56,142,60,0.10); }
.sz-sb-option-avatar {
  width: 24px; height: 24px; border-radius: 999px; object-fit: cover; flex-shrink: 0;
}
.sz-sb-option-avatar--initial {
  display: inline-flex; align-items: center; justify-content: center;
  background: #FF5500; color: #FFFFFF; font-size: 11px; font-weight: 600;
}
.sz-sb-option-text {
  flex: 1; min-width: 0;
  font-size: 13px; color: #000000;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.sz-sb-multi-empty { padding: 8px; font-size: 12px; color: #6B7280; text-align: center; }

/* Skeleton loader pro dropdown enquanto agents/get carrega */
.sz-sb-multi-option--skeleton { cursor: default; pointer-events: none; }
.sz-sb-multi-option--skeleton:hover { background: transparent; }
.sz-sb-skel {
  display: inline-block;
  background: linear-gradient(90deg, #E5E7EB 0%, #F3F4F6 50%, #E5E7EB 100%);
  background-size: 200% 100%;
  animation: sz-sb-shimmer 1.4s ease-in-out infinite;
  border-radius: 999px;
}
.sz-sb-skel--avatar { width: 24px; height: 24px; flex-shrink: 0; }
.sz-sb-skel--text { flex: 1; height: 12px; border-radius: 6px; }
@keyframes sz-sb-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.sz-sb-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.sz-sb-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: #000000;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.sz-sb-pill:hover { background: rgba(56,142,60,0.08); }
.sz-sb-pill.active {
  background: #DFEFE3;
  border-color: transparent;
}

.sz-sb-date-row {
  display: flex;
  gap: 8px;
}
.sz-sb-date-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.sz-sb-date-prefix {
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #6B7280;
}

.sz-sb-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.sz-sb-btn {
  height: 32px;
  padding: 4px 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
}
.sz-sb-btn--ghost {
  background: transparent;
  border: 1.5px solid #D4D4D4;
  color: #000000;
}
.sz-sb-btn--ghost:hover { background: rgba(56,142,60,0.08); }
.sz-sb-btn--primary {
  background: #388E3C;
  border: 1.5px solid #388E3C;
  color: #FFFFFF;
}
.sz-sb-btn--primary:hover { background: #2E7D32; border-color: #2E7D32; }

/* Dark sidebar */
.dark .sz-search-sidebar {
  background: #1C1D1F;
  border-left-color: #2A2C30;
}
.dark .sz-sb-title { color: #FFFFFF; }
.dark .sz-sb-label { color: #FFFFFF; }
.dark .sz-sb-multi-field {
  background: #242528;
  border-color: #303138;
}
.dark .sz-sb-placeholder { color: #92939A; }
.dark .sz-sb-multi-chevron { color: #92939A; }
.dark .sz-sb-tag-agent { background: rgba(255,255,255,0.08); }
.dark .sz-sb-tag-text { color: #FFFFFF; }
.dark .sz-sb-tag-x { color: #B0B4FF; }
.dark .sz-sb-multi-dropdown {
  background: #1F2125;
  border-color: #303138;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}
.dark .sz-sb-multi-option:hover { background: rgba(138,206,138,0.08); }
.dark .sz-sb-multi-option--on { background: rgba(56,142,60,0.18); }
.dark .sz-sb-option-text { color: #E5E7EB; }
.dark .sz-sb-multi-empty { color: #9CA3AF; }
.dark .sz-sb-skel {
  background: linear-gradient(90deg, #303138 0%, #3A3B42 50%, #303138 100%);
  background-size: 200% 100%;
}
.dark .sz-sb-pill {
  background: transparent;
  border-color: #3D3F43;
  color: #9797A0;
}
.dark .sz-sb-pill:hover { background: rgba(138,206,138,0.08); color: #E5E7EB; }
.dark .sz-sb-pill.active {
  background: #194121;
  border-color: transparent;
  color: #FFFFFF;
}
.dark .sz-sb-date-prefix { color: #CDCDCD; }
.dark .sz-sb-btn--ghost {
  border-color: #3E3E43;
  color: #E5E7EB;
}
.dark .sz-sb-btn--ghost:hover { background: #1C1E22; }

.dark .sz-fullsearch-toolbar { border-color: #363840; }
.dark .sz-fullsearch-back { color: #9CA3AF; }
.dark .sz-fullsearch-back:hover { background: #363840; }
.dark .sz-fullsearch-input-compact { background: #282A2E; border-color: #363840; }
.dark .sz-fullsearch-input-compact:focus-within { border-color: #59AA59; }
.dark .sz-fullsearch-input-compact svg { color: #6B7280; }
.dark .sz-fullsearch-input { color: #E5E7EB; }
.dark .sz-fullsearch-input::placeholder { color: #6B7280; }
.dark .sz-fullsearch-clear { background: #363840; color: #9CA3AF; }
.dark .sz-fullsearch-clear:hover { background: #4B5563; }
/* SZ pills — dark: paleta padrão do projeto (#17191C bg, #194121 verde active). */
.dark .sz-fullsearch-tab {
  background: #17191C;
  border-color: #2A2C30;
  color: #E5E7EB;
}
.dark .sz-fullsearch-tab:hover { background: #1C1E22; }
.dark .sz-fullsearch-tab.active {
  background: #194121;
  border-color: transparent;
  color: #FFFFFF;
}
.dark .sz-fullsearch-tab-count {
  background: #FFFFFF;
  color: #000000;
}

.dark .sz-fullsearch-section-header { color: #E5E7EB; }
.dark .sz-fullsearch-section-count { background: #FFFFFF; color: #000000; }
.dark .sz-fullsearch-item-primary { color: #E5E7EB; }
.dark .sz-fullsearch-id { color: #9CA3AF; }
.dark .sz-fullsearch-item-meta { color: #9CA3AF; }
.dark .sz-meta-sep { color: #6B7280; }
.dark .sz-channel-icon { color: #9CA3AF; }
.dark .sz-channel-icon[data-type*="whatsapp"] { color: #25D366; }
.dark .sz-fullsearch-avatar-msg { background: #194121; color: #5AB265; }
.dark .sz-hl { background: #194121; color: #5AB265; }
.dark .sz-fullsearch-section-count { background: #363840; color: #9CA3AF; }
.dark .sz-fullsearch-item:hover { background: #363840; }
.dark .sz-fullsearch-avatar { background: #4B5563; color: #D1D5DB; }
.dark .sz-fullsearch-avatar-msg { background: rgba(56,142,60,0.15); color: #4ADE80; }
.dark .sz-fullsearch-item-name { color: #E5E7EB; }
.dark .sz-fullsearch-item-meta { color: #6B7280; }
.dark .sz-fullsearch-conv-id { color: #6B7280; }
.dark .sz-fullsearch-viewmore { color: #4ADE80; }
.dark .sz-fullsearch-viewmore:hover { background: rgba(56,142,60,0.1); }
.dark .sz-fullsearch-loadmore button { background: #282A2E; border-color: #363840; color: #E5E7EB; }
.dark .sz-fullsearch-loadmore button:hover { background: #363840; }
</style>
